import { Link } from 'react-router-dom';
import { V2Card, V2CardHeader } from '@sakhtar/design-system/react';
import { addDays, businessDate, compareBusinessDates, systemClock } from '@sakhtar/calendar';
import type { AmountDisplayUnit } from '../../lib/finance-display.js';
import { formatRialFa, openItemHref } from '../../lib/finance-display.js';
import { displayJalaliDate } from '../../lib/date/displayJalali.js';
import type { AgingItem } from '../../lib/types/finance.js';
import { PartyLabel } from './PartyLabel.js';
import { sumRial, type PartyBalanceRow } from './OpenItemAggregates.js';

const JALALI_MONTHS = [
  'فروردین',
  'اردیبهشت',
  'خرداد',
  'تیر',
  'مرداد',
  'شهریور',
  'مهر',
  'آبان',
  'آذر',
  'دی',
  'بهمن',
  'اسفند',
] as const;

/** Day + month name from the shared Jalali display string (`YYYY/MM/DD`, Persian digits). */
function dueDateTile(dueDate: string): { readonly day: string; readonly month: string } {
  const [, month, day] = displayJalaliDate(dueDate).split('/');
  const monthIndex = Number(
    (month ?? '').replace(/[۰-۹]/g, (d) => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(d))),
  );
  return { day: day ?? '—', month: JALALI_MONTHS[monthIndex - 1] ?? '' };
}

/**
 * UI_REFERENCE_SOURCE:
 * section: Sakhtar Cloud ERP v2.dc.html — /t/kimia/finance, finAp «برنامهٔ پرداخت
 *   این هفته» + «مانده تأمین‌کنندگان» (sk-prog share bars).
 * status: PARTIAL_REAL — the schedule is the open/partial AP items whose
 *   `dueDate` falls in [as-of, as-of+7] (and overdue ones are listed first in
 *   danger tone), straight from `GET .../finance/ap/aging`. Omitted: the
 *   reference's «مسدود تا رفع اختلاف» blocking state — it depends on 3-way
 *   match (PO · goods receipt · invoice), which no endpoint in
 *   apps/api/src/finance or packages/modules/accounting exposes.
 */
export function OpenItemsPaymentSchedule({
  base,
  slug,
  items,
  partyRows,
  unit,
  asOfDate,
}: {
  readonly base: string;
  readonly slug: string;
  readonly items: readonly AgingItem[];
  readonly partyRows: readonly PartyBalanceRow[];
  readonly unit: AmountDisplayUnit;
  /** Same as-of date the aging items were fetched for (YYYY-MM-DD). */
  readonly asOfDate: string;
}) {
  const isToday = asOfDate === systemClock.today();
  const weekEnd = addDays(businessDate(asOfDate), 7);
  const scheduled = items
    .filter((item) => {
      const due = businessDate(item.dueDate.slice(0, 10));
      return compareBusinessDates(due, weekEnd) <= 0;
    })
    .sort((a, b) => a.dueDate.localeCompare(b.dueDate));
  const scheduledTotal = sumRial(scheduled.map((i) => i.openBalanceRial));
  const grandTotal = sumRial(partyRows.map((r) => r.balanceRial));
  const topParties = partyRows.slice(0, 5);

  return (
    <V2Card>
      <V2CardHeader
        icon="calendar"
        title={isToday ? 'برنامهٔ پرداخت این هفته' : 'برنامهٔ پرداخت ۷ روز پس از تاریخ مبنا'}
        subtitle={`${scheduled.length.toLocaleString('fa-IR')} قلم · ${formatRialFa(
          scheduledTotal.toString(),
          unit,
        )} · شامل سررسید گذشته‌ها`}
      />
      {scheduled.length === 0 ? (
        <div style={{ padding: '14px', fontSize: 13, color: 'var(--sk-fg-muted)' }}>
          قلمی تا یک هفتهٔ آینده سررسید نمی‌شود.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {scheduled.map((item) => {
            const tile = dueDateTile(item.dueDate);
            const overdue = item.daysPastDue > 0;
            return (
              <div
                key={item.openItemId}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '9px 14px',
                  borderBottom: '1px solid var(--sk-divider)',
                }}
              >
                <span
                  className="sk-num"
                  style={{
                    width: 44,
                    flex: '0 0 44px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    padding: '4px 0',
                    borderRadius: 8,
                    background: overdue ? 'var(--sk-danger-soft)' : 'var(--sk-bg-sunken)',
                    color: overdue ? 'var(--sk-danger)' : undefined,
                    fontSize: 15,
                    fontWeight: 800,
                    lineHeight: 1.1,
                  }}
                >
                  {tile.day}
                  <span
                    style={{
                      fontSize: 10,
                      fontWeight: 500,
                      color: overdue ? undefined : 'var(--sk-fg-muted)',
                    }}
                  >
                    {tile.month}
                  </span>
                </span>
                <div
                  style={{
                    flex: 1,
                    minWidth: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                  }}
                >
                  <span style={{ fontSize: 13, fontWeight: 600 }}>
                    <PartyLabel slug={slug} partyId={item.partyId} />
                  </span>
                  <Link
                    to={openItemHref(base, 'ap', item.openItemId)}
                    className="sk-num ltr"
                    style={{
                      fontSize: 11.5,
                      color: overdue ? 'var(--sk-danger)' : 'var(--sk-fg-muted)',
                    }}
                  >
                    {item.sourceReference || '—'}
                    {overdue ? ` · ${item.daysPastDue.toLocaleString('fa-IR')} روز گذشته` : ''}
                  </Link>
                </div>
                <span className="sk-num" style={{ fontSize: 13, fontWeight: 700 }}>
                  {formatRialFa(item.openBalanceRial, unit)}
                </span>
              </div>
            );
          })}
        </div>
      )}
      {topParties.length > 0 && grandTotal > 0n ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '12px 14px' }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--sk-fg-muted)' }}>
            سهم از مانده تأمین‌کنندگان
          </span>
          {topParties.map((row) => {
            const share = Number((BigInt(row.balanceRial) * 100n) / grandTotal);
            return (
              <div key={row.id} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 8,
                    fontSize: 12.5,
                  }}
                >
                  <span style={{ fontWeight: 600 }}>
                    <PartyLabel slug={slug} partyId={row.partyId} />
                  </span>
                  <span className="sk-num" style={{ fontWeight: 700 }}>
                    {formatRialFa(row.balanceRial, unit)}
                  </span>
                </div>
                <div
                  className="sk-prog"
                  role="progressbar"
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={share}
                >
                  <div className="sk-prog__fill" style={{ width: `${share}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      ) : null}
    </V2Card>
  );
}
